import { useContext, createContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { GET_IN_USER_COOKIE_KEY } from 'utils/constants';
import { IWriteResponseLogin } from 'hooks/use-login';

export type UserProps = {
  id: string;
  name: string;
  social_name: string;
  identity: string;
  mobile: string;
  email: string;
  birthday: string;
  gender: string;
  last_seen: string;
};

export type SessionProps =
  | (IWriteResponseLogin & {
      phone?: string;
      email?: string;
      registerSession?: string;
    })
  | null;

export type AuthContextProps = {
  user: UserProps | null;
  session: SessionProps;
  isLoginOpen: boolean;
  isRegisterOpen: boolean;
  isResendCodeOpen: boolean;
  isLoginByEmailOpen: boolean;
  isConfirmEmailOpen: boolean;
  isCheckOTPCodeOpen: boolean;
  isUserDuplicateOpen: boolean;
  startedLoginByEmailOrSMS: string | null;
  handleIsOpenUserDuplicateModal: (isOpen: boolean) => void;
  setAuthSession: (props: SessionProps) => void;
  setAuthUser: (props: UserProps | null) => void;
  handleIsOpenLoginModal: (isOpen: boolean) => void;
  handleIsOpenRegisterModal: (isOpen: boolean) => void;
  handleIsOpenResendCodeModal: (isOpen: boolean) => void;
  handleIsOpenCheckOTPCodeOpen: (isOpen: boolean) => void;
  handleIsOpenLoginByEmailModal: (isOpen: boolean) => void;
  handleIsOpenConfirmEmailModal: (isOpen: boolean) => void;
  handleChangeStartedLoginByEmailOrSMS: (flowName: string | null) => void;
};

export const AuthContextDefaultValues = {
  user: null,
  session: null,
  isLoginOpen: false,
  isRegisterOpen: false,
  isResendCodeOpen: false,
  startedLoginByEmailOrSMS: null,
  isCheckOTPCodeOpen: false,
  isLoginByEmailOpen: false,
  isConfirmEmailOpen: false,
  isUserDuplicateOpen: false,
  setAuthSession: () => null,
  setAuthUser: () => null,
  handleIsOpenConfirmEmailModal: () => null,
  handleIsOpenUserDuplicateModal: () => null,
  handleIsOpenLoginModal: () => null,
  handleIsOpenRegisterModal: () => null,
  handleIsOpenResendCodeModal: () => null,
  handleIsOpenCheckOTPCodeOpen: () => null,
  handleIsOpenLoginByEmailModal: () => null,
  handleChangeStartedLoginByEmailOrSMS: () => null,
};

export const AuthContext = createContext<AuthContextProps>(
  AuthContextDefaultValues
);

export type AuthProviderProps = {
  children: React.ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserProps | null>(null);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isConfirmEmailOpen, setIsConfirmEmailOpen] = useState(false);
  const [isUserDuplicateOpen, setIsUserDuplicateOpen] = useState(false);
  const [isCheckOTPCodeOpen, setIsCheckOTPCodeOpen] = useState(false);
  const [session, setSession] = useState<SessionProps>(null);
  const [startedLoginByEmailOrSMS, setStartedLoginByEmailOrSMS] = useState<
    string | null
  >(null);
  const [isResendCodeOpen, setIsResendCodeOpen] = useState(false);
  const [isLoginByEmailOpen, setIsLoginByEmailOpen] = useState(false);
  const me = Cookies.get(GET_IN_USER_COOKIE_KEY);

  useEffect(() => {
    if (me) setUser(JSON.parse(me));
  }, [me]);

  const setAuthSession = (session: SessionProps) => {
    setSession(session);
  };

  const setAuthUser = (user: UserProps | null) => {
    setUser(user);
  };

  const handleIsOpenLoginModal = (isOpen: boolean) => setIsLoginOpen(isOpen);

  const handleIsOpenRegisterModal = (isOpen: boolean) =>
    setIsRegisterOpen(isOpen);

  const handleIsOpenConfirmEmailModal = (isOpen: boolean) =>
    setIsConfirmEmailOpen(isOpen);

  const handleChangeStartedLoginByEmailOrSMS = (flowName: string | null) =>
    setStartedLoginByEmailOrSMS(flowName);

  const handleIsOpenResendCodeModal = (isOpen: boolean) =>
    setIsResendCodeOpen(isOpen);

  const handleIsOpenLoginByEmailModal = (isOpen: boolean) =>
    setIsLoginByEmailOpen(isOpen);

  const handleIsOpenUserDuplicateModal = (isOpen: boolean) =>
    setIsUserDuplicateOpen(isOpen);

  const handleIsOpenCheckOTPCodeOpen = (isOpen: boolean) =>
    setIsCheckOTPCodeOpen(isOpen);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        setAuthUser,
        isLoginOpen,
        isRegisterOpen,
        isResendCodeOpen,
        isLoginByEmailOpen,
        isConfirmEmailOpen,
        isCheckOTPCodeOpen,
        isUserDuplicateOpen,
        startedLoginByEmailOrSMS,
        setAuthSession,
        handleIsOpenLoginModal,
        handleIsOpenRegisterModal,
        handleIsOpenResendCodeModal,
        handleIsOpenCheckOTPCodeOpen,
        handleIsOpenConfirmEmailModal,
        handleIsOpenLoginByEmailModal,
        handleIsOpenUserDuplicateModal,
        handleChangeStartedLoginByEmailOrSMS,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
